import React, { useEffect, useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  Remove as MinusIcon,
  ArrowDropDown,
  ArrowDropUp,
} from '@mui/icons-material';
import DoneIcon from '@mui/icons-material/Done';

interface Policy {
  id: string;
  displayName: string;
  displayDesc: string;
}

interface ListPermission {
  groupName: string;
  policies: Policy[];
}

interface Props {
  permission: ListPermission[];
  Ids: string[];
  assignPermission: string[];
  setAssignPermission: (permissions: string[]) => void;
}

const Select: React.FC<Props> = ({
  permission,
  Ids,
  assignPermission,
  setAssignPermission,
}) => {
  const [expanded, setExpanded] = useState<number[]>([]);
  const [allID, setAllID] = useState<string[]>([]);
  const [listPermission, setListPermission] = useState<ListPermission[]>([]);

  function arraysAreEqual<T>(array1: T[], array2: T[]): boolean {
    if (array1 && array2) {
      if (array1.length !== array2.length) {
        return false;
      }

      const sortedArray1 = array1.slice().sort();
      const sortedArray2 = array2.slice().sort();

      for (let i = 0; i < sortedArray1.length; i++) {
        if (sortedArray1[i] !== sortedArray2[i]) {
          return false;
        }
      }

      return true;
    } else {
      return false;
    }
  }

  function checkArrayvalue<T>(array1: T[], array2: T[], type: string): boolean {
    if (type === 'group') {
      const firstSet = new Set(array1);

      for (const item of array2) {
        if (firstSet.has(item)) {
          return true;
        }
      }
      return false;
    } else {
      const firstSet = new Set(array1);

      for (const item of array2) {
        if (!firstSet.has(item)) {
          return false;
        }
      }
      return true;
    }
  }

  const PermissionGroup: React.FC<{
    group: ListPermission;
    assignPermission: string[];
    index: number;
  }> = ({ group, assignPermission, index }) => {
    const getgroupids = () => {
      return group?.policies
        ? group?.policies.map((policy: Policy) => policy.id)
        : [];
    };

    const addGroupPermission = (groupid: string[]) => {
      setAssignPermission([...assignPermission, ...groupid]);
    };

    const removeGroupPermission = (groupid: string[]) => {
      setAssignPermission(
        assignPermission.filter((item) => !groupid.includes(item)),
      );
    };

    const addpolicyid = (id: string) => {
      setAssignPermission([...assignPermission, id]);
    };

    const removepolicyid = (id: string) => {
      setAssignPermission(
        assignPermission.filter((item) => !id.includes(item)),
      );
    };

    const toggleExpanded = () => {
      setExpanded((prevExpanded) => {
        if (prevExpanded.includes(index)) {
          return prevExpanded.filter((item) => item !== index);
        } else {
          return [...prevExpanded, index];
        }
      });
    };

    return (
      <>
        <TableRow key={group.groupName}>
          <TableCell colSpan={2}>
            {checkArrayvalue(assignPermission, getgroupids(), 'group') ? (
              <IconButton
                className="edit_role_selection-select_box"
                onClick={() => {
                  removeGroupPermission(getgroupids());
                }}
              >
                {checkArrayvalue(assignPermission, getgroupids(), 'nested') ? (
                  <DoneIcon className="edit_role_selection-select_box-icons" />
                ) : (
                  <MinusIcon className="edit_role_selection-select_box-icons" />
                )}
              </IconButton>
            ) : (
              <IconButton
                className="edit_role_selection-unselect_box"
                onClick={() => {
                  addGroupPermission(getgroupids());
                }}
              ></IconButton>
            )}
            {group.groupName}
            <IconButton
              className="edit_role_selection-expanded_btn"
              onClick={() => toggleExpanded()}
            >
              {expanded.includes(index) ? <ArrowDropUp /> : <ArrowDropDown />}
            </IconButton>
          </TableCell>
        </TableRow>
        {expanded.includes(index) &&
          group.policies &&
          group.policies.length > 0 &&
          group.policies.map((policy) => (
            <TableRow key={policy.id}>
              <TableCell className="edit_role_selection-permissions_group-child">
                {assignPermission.includes(policy.id) ? (
                  <IconButton
                    className="edit_role_selection-select_box"
                    onClick={() => {
                      removepolicyid(policy.id);
                    }}
                  >
                    <DoneIcon className="edit_role_selection-select_box-icons" />
                  </IconButton>
                ) : (
                  <IconButton
                    className="edit_role_selection-unselect_box"
                    onClick={() => {
                      addpolicyid(policy.id);
                    }}
                  ></IconButton>
                )}
                <Tooltip title={policy.displayDesc} placement="top">
                  <span className="edit_role_selection-tooltip">
                    {policy.displayName}
                  </span>
                </Tooltip>
              </TableCell>
              <TableCell align="right"></TableCell>
            </TableRow>
          ))}
      </>
    );
  };

  const PermissionTable: React.FC<{
    listPermission: ListPermission[];
    assignPermission: string[];
  }> = ({ listPermission, assignPermission }) => {
    return (
      <>
        {listPermission?.length > 0 &&
          listPermission.map((group: ListPermission, index: number) => (
            <PermissionGroup
              key={index}
              group={group}
              assignPermission={assignPermission}
              index={index}
            />
          ))}
      </>
    );
  };

  useEffect(() => {
    setListPermission(permission);
    setAllID(Ids);
  }, [permission, Ids]);

  return (
    <TableContainer component={Paper} className="customselect_permission">
      <Table>
        <TableHead>
          <TableCell className="edit_role_selection">
            {assignPermission?.length < 1 ? (
              <IconButton
                className="edit_role_selection-unselect_box"
                onClick={() => setAssignPermission(allID)}
              ></IconButton>
            ) : (
              <IconButton
                className="edit_role_selection-select_box"
                onClick={() => setAssignPermission([])}
              >
                {arraysAreEqual(allID, assignPermission) ? (
                  <DoneIcon className="edit_role_selection-select_box-icons" />
                ) : (
                  <MinusIcon className="edit_role_selection-select_box-icons" />
                )}
              </IconButton>
            )}
            Permissions
          </TableCell>
        </TableHead>
        <TableBody>
          <PermissionTable
            listPermission={listPermission}
            assignPermission={assignPermission}
          />
        </TableBody>
      </Table>
    </TableContainer>
  );
};

const MemoizedComponent = React.memo(Select);
export default MemoizedComponent;
